import { useEffect, useRef, useState } from 'react';

/**
 * Animates a number from 0 → target over `duration` ms using easeOutCubic.
 * Re-fires whenever the `trigger` prop changes — used to replay counters
 * after a tab switch or login.
 */
export function useCountUp(
  target: number,
  { duration = 1100, decimals = 0, trigger = 0 }: { duration?: number; decimals?: number; trigger?: number } = {},
) {
  const [val, setVal] = useState(0);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(0);

  useEffect(() => {
    startRef.current = null;
    fromRef.current = 0;
    let raf = 0;
    const tick = (t: number) => {
      if (startRef.current === null) startRef.current = t;
      const p = Math.min(1, (t - startRef.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(fromRef.current + (target - fromRef.current) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, trigger]);

  return decimals === 0 ? Math.round(val) : +val.toFixed(decimals);
}
